import Link from "next/link";
import { Category } from "../../types/category";
import { MAX_CATEGORY_DEPTH } from "../../core/storefrontMenuCategory.core";

interface StorefrontMenuCategoryDropdownProps {
  category: Category;
}

function getCategoryHref(slug: string) {
  return `/category/${encodeURIComponent(slug)}/1`;
}

function CategoryDropdownColumn({ category, depth }: { category: Category; depth: number }) {
  return (
    <div className="min-w-[140px] max-w-[200px]">
      <Link
        href={getCategoryHref(category.slug)}
        className="block text-sm font-semibold text-gray-900 hover:text-amber-600 mb-2"
      >
        {category.name}
      </Link>
      {category.children?.length > 0 && depth < MAX_CATEGORY_DEPTH && (
        <CategoryDropdownNestedList categories={category.children} depth={depth + 1} />
      )}
    </div>
  );
}

function CategoryDropdownNestedList({ categories, depth }: { categories: Category[]; depth: number }) {
  if (depth > MAX_CATEGORY_DEPTH) return null;

  const textClass = depth <= 2 ? "text-sm text-gray-600" : "text-xs text-gray-500";

  return (
    <ul className={depth > 2 ? "ml-3 mt-1 space-y-1 border-l border-gray-100 pl-2" : "space-y-1"}>
      {categories.map((category) => (
        <li key={category.id}>
          <Link href={getCategoryHref(category.slug)} className={`block hover:text-amber-600 ${textClass}`}>
            {category.name}
          </Link>
          {category.children?.length > 0 && depth < MAX_CATEGORY_DEPTH && (
            <CategoryDropdownNestedList categories={category.children} depth={depth + 1} />
          )}
        </li>
      ))}
    </ul>
  );
}

export function StorefrontMenuCategoryDropdown({ category }: StorefrontMenuCategoryDropdownProps) {
  if (!category.children?.length) return null;

  return (
    <div className="absolute left-0 right-0 top-full bg-white shadow-lg border-t border-gray-100 z-50">
      <div className="max-w-7xl mx-auto px-8 md:px-12 py-6">
        <div className="flex flex-wrap gap-x-8 gap-y-6">
          {category.children.map((child) => (
            <CategoryDropdownColumn key={child.id} category={child} depth={1} />
          ))}
        </div>
      </div>
    </div>
  );
}
